import { ImagePicker } from 'components/reusable/ImagePicker';
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet, TextInput } from 'react-native';

import { Coordinate } from 'types/map';
import { MarkerType } from 'types/marker';
import { EditCoordinate } from './EditCoordinate';
import { dataIsValid } from 'utils/helpers';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { change } from 'hooks/markersSlice';
import { initEdit, stopEdit } from 'hooks/editSlice';

function MarkerRow({
  marker,
  styles,
  deleteMarker,
  save,
  cancelChanges,
  hasChanged,
  displayError,
}: {
  marker: MarkerType;
  styles: StyleSheet.NamedStyles<any>;
  deleteMarker: (marker: MarkerType) => void;
  save: (marker: MarkerType) => void;
  cancelChanges: (marker: MarkerType) => void;
  hasChanged: (marker: MarkerType) => boolean;
  displayError: (msg: string) => void;
}) {
  const changed = hasChanged(marker);
  const dispatch = useAppDispatch();
  const edit = useAppSelector((state) => state.edit);
  const form = {
    title: {
      required: true,
      errorMessage: 'Title is required.',
      error: null,
    },
    description: {
      required: true,
      errorMessage: 'Description is required.',
      error: null,
    },
    latlng: {
      required: true,
      errorMessage: 'Location is required.',
      error: null,
    },
  };
  const [formValidation, setFormValidation] = useState({ ...form });

  const onDataChange = useCallback(
    (key: string, value: string | Coordinate) => {
      if (value !== marker[key]) {
        dispatch(
          change({
            marker: {
              ...marker,
              [key]: value,
            },
          }),
        );
      }
    },
    [dispatch, marker],
  );

  async function getImage(
    key: string,
    maxSize: number,
    image: string,
    size: number,
  ) {
    if (image) {
      if (maxSize && size && size > maxSize) {
        const kb = Math.round((Math.ceil(maxSize / 4) * 3) / 1000);
        displayError(`Image is too large, please select one less than ${kb}kb`);
      } else {
        onDataChange(key, image);
      }
    }
  }

  useEffect(() => {
    if (!marker.latlng && edit.value.coords) {
      onDataChange('latlng', edit.value.coords[0]);
      dispatch(stopEdit());
    }
  }, [edit, marker, onDataChange, dispatch]);

  return (
    <View key={marker.id} style={styles.row}>
      <Text style={styles.label}>Title:</Text>
      <TextInput
        style={[
          styles.input,
          formValidation.title.error ? styles.inputError : null,
        ]}
        value={marker.title}
        onChangeText={(text) => onDataChange('title', text)}
      />
      {formValidation.title.error && (
        <Text style={styles.error}>{formValidation.title.errorMessage}</Text>
      )}

      <Text style={styles.label}>Description:</Text>
      <TextInput
        style={[
          styles.input,
          formValidation.description.error ? styles.inputError : null,
        ]}
        multiline
        numberOfLines={3}
        value={marker.description}
        onChangeText={(text) => onDataChange('description', text)}
      />
      {formValidation.description.error && (
        <Text style={styles.error}>
          {formValidation.description.errorMessage}
        </Text>
      )}

      <Text style={styles.label}>Image:</Text>
      <ImagePicker
        onImageChange={(image, size) => getImage('image', null, image, size)}
        image={marker.image}
        url={true}
      />

      <Text style={styles.label}>Icon:</Text>
      <ImagePicker
        onImageChange={(image, size) =>
          getImage('markerIcon', 5400, image, size)
        }
        image={marker.markerIcon}
        type="icon"
      />

      <EditCoordinate
        coord={marker.latlng}
        onClear={() => {
          onDataChange('latlng', null);
        }}
      />
      {!marker.latlng && <Text style={styles.action}>Select area on map</Text>}
      {formValidation.latlng.error && (
        <Text style={styles.error}>{formValidation.latlng.errorMessage}</Text>
      )}

      <View style={styles.inlineButtonContainer}>
        <Pressable onPress={() => deleteMarker(marker)}>
          <Text style={[styles.inlineButton, styles.error]}>Delete</Text>
        </Pressable>
        {changed && (
          <Pressable onPress={() => cancelChanges(marker)}>
            <Text style={styles.inlineButton}>Cancel</Text>
          </Pressable>
        )}
        {changed && (
          <Pressable
            onPress={() => {
              if (dataIsValid(formValidation, setFormValidation, marker)) {
                save(marker);
                dispatch(stopEdit());
              }
            }}
          >
            <Text style={[styles.inlineButton, styles.success]}>Save</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

export function EditMarkers({
  styles,
  addMarker,
  deleteMarker,
  saveMarker,
  displayError,
  cancelChanges,
  hasChanged,
}: {
  styles: StyleSheet.NamedStyles<any>;
  addMarker: () => void;
  deleteMarker: (marker: MarkerType) => void;
  saveMarker: (marker: MarkerType) => Promise<void>;
  displayError: (msg: string) => void;
  cancelChanges: (marker: MarkerType) => void;
  hasChanged: (marker: MarkerType) => boolean;
}) {
  const dispatch = useAppDispatch();
  const markers = useAppSelector((state) => state.markers);
  function save(marker: MarkerType) {
    dispatch(change({ marker }));
    saveMarker(marker).catch(() =>
      displayError('Marker not saved, please try again'),
    );
  }

  return (
    <View>
      {markers &&
        markers.value.map((marker) => (
          <MarkerRow
            key={marker.id}
            marker={marker}
            styles={styles}
            deleteMarker={deleteMarker}
            save={save}
            cancelChanges={cancelChanges}
            hasChanged={hasChanged}
            displayError={displayError}
          />
        ))}
      <View style={styles.buttonContainer}>
        <Pressable
          onPress={() => {
            dispatch(initEdit());
            addMarker();
          }}
          style={styles.button}
        >
          <Text style={styles.buttonText}>Add Marker</Text>
        </Pressable>
      </View>
    </View>
  );
}
